// src/app/services/brand/resolve-customer.ts
//
// ONE place that turns the selected brand NAME into a Deep Signal customer id.
//
// Nav carries a brand string; every measurement surface needs a customer. Three
// call sites used to do that hop themselves, each with its own bidirectional
// `includes()` match, and the backend did it a fourth time inside the query.
// That is how "Sunbit" reached a customer named "Sunbit Continuity Test": a
// substring match with a most-recent-run tie-break has no way to prefer the
// real client.
//
// The rule here: resolve ONCE, then pass the id. Callers that hold an id must
// send the id and never re-resolve — the resolution is a guess and the id is
// not. The server ranks the candidates (brand_name exact, then display-name
// exact, then substring) and applies the user's brand scope, so this picks the
// first row rather than re-implementing the ranking client-side.
//
// null means "no Deep Signal customer for this brand" — a real state, not an
// error: a brand can be monitored without ever having been scored. Callers
// must handle it rather than falling back to another brand's data.

import { listDeepSignalCustomers, type DSCustomerSummary } from "@/api/deep-signal-api";

export type ResolvedBrandCustomer = DSCustomerSummary;

// Resolution changes only when a customer is created or renamed, so cache the
// in-flight promise per brand for the same short window the series data uses.
const _TTL_MS = 60_000;
const _cache = new Map<string, { at: number; p: Promise<ResolvedBrandCustomer | null> }>();

if (typeof window !== "undefined") {
  window.addEventListener("analytics:refresh", () => _cache.clear());
}

export function clearBrandCustomerCache(): void {
  _cache.clear();
}

/**
 * The Deep Signal customer for a brand name, or null when the brand has none.
 * Never throws: a failed lookup resolves to null so the caller degrades to its
 * own empty state instead of showing whatever loaded last.
 */
export function resolveBrandCustomer(
  brandName: string | undefined | null,
): Promise<ResolvedBrandCustomer | null> {
  const brand = (brandName || "").trim();
  if (!brand) return Promise.resolve(null);

  const key = brand.toLowerCase();
  const now = Date.now();
  const hit = _cache.get(key);
  if (hit && now - hit.at < _TTL_MS) return hit.p;

  const p = listDeepSignalCustomers({ brand })
    .then(({ customers }) => {
      const best = customers?.[0] ?? null;
      // Rank 2 is the substring tier: the brand string did not actually name
      // this customer, it only appeared inside its name. Worth a breadcrumb —
      // it is the condition the naming cleanup is meant to remove.
      if (best && (best.match_rank ?? 2) >= 2) {
        console.warn(
          `[brand] "${brand}" matched customer ${best.id} ("${best.brand_name}") ` +
          `only as a substring${customers.length > 1 ? ` — ${customers.length} candidates` : ""}`,
        );
      }
      return best;
    })
    .catch(() => {
      _cache.delete(key);
      return null;
    });

  _cache.set(key, { at: now, p });
  return p;
}

/** The customer id alone, for callers that need nothing else. */
export function resolveCustomerId(
  brandName: string | undefined | null,
): Promise<number | null> {
  return resolveBrandCustomer(brandName).then((c) => c?.id ?? null);
}
